// lib
import React, { useReducer, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { createPortal } from 'react-dom'

import ADND from '@aytm/lib/adnd/ADND'

import { REPORTS_HELP } from '@aytm/app/stats/page/sidebar/components/lib/helps'

import { generateUID } from '@aytm/app/stats/lib/util/helpers'
import {
  STATIC_REPORTS_IDS,
  VIRTUAL_REPORTS_IDS,
  dropListItemDefault,
  dropListItemAutomated,
  dropListItemGuided,
  dropListItemOther,
  addListItem,
  addSkipperGuidedListItem,
  ensureSkipperGuidedGeneratingDraft,
  adoptSkipperGuidedDraftReport,
  markSkipperGuidedDraftReady
} from '@aytm/app/stats/page/sidebar/reports/actions'

import { reportsReducer } from '@aytm/app/stats/page/sidebar/reports/reducers'

import {
  GUIDED_REPORT_ERROR_NOTICE,
  SKIPPER_REPORT_FEATURE
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportsConst'
import {
  isSkipperGuidedReport,
  isSkipperGuidedDraftReport,
  isSkipperReportPurchased
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReport'
import {
  MAX_INFLIGHT_REPORTS,
  GuidedReportStatusPoller
} from '@aytm/app/stats/page/sidebar/reports/lib/skipperGuidedReportStatus'

import { openGuidedReportsChat } from '@aytm/app/stats/stores/guidedReportsEntry'
import { createGuidedReportGenerationPoll, generationReportId } from '@aytm/app/stats/stores/guidedReportGenerationPoll'
import { generationPollKey, createGuidedReportJobPoll } from '@aytm/app/stats/stores/guidedReportJobPoll'
import {
  TRACKING_RANK,
  claimGuidedReportTracking,
  releaseGuidedReportTracking
} from '@aytm/app/stats/stores/guidedReportTracking'

// components
import { Button } from 'primereact/button'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import SkipperGuidedReportButton from '@aytm/app/stats/page/sidebar/reports/components/SkipperGuidedReportButton'

import Report from '@aytm/app/stats/page/sidebar/reports/Report'
import ReportControls from '@aytm/app/stats/page/sidebar/reports/ReportControls'

import SidebarPopupWarning from '@aytm/app/stats/page/sidebar/components/SidebarPopupWarning'

// styles
import style from '@aytm/app/stats/page/sidebar/reports/styles/ReportsPanel.module.scss'
import headerStyle from '@aytm/app/stats/page/components/styles/StickyHeader.module.scss'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'
import { getFeature } from '@aytm/app/stats/lib/stat-page'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

export const DataContext = React.createContext()

const ReportsPanel = ({
  model,
  report,
  features,
  isHidden,
  getReportFilters,
  onModeChange,
  onPanelChange,
  onReportsChange,
  onReportsError,
  onReportIdChange,
  repaintReport
}) => {
  const { mode, reports, reportId, isAdmin, isSurveyGuest, isSurveyViewer, isSurveyCompleted, isQAMode } = model

  const disabled = isQAMode
  // An account whose payload carries no entry for the addon reads as neither hidden, purchasable
  // nor locked -- the same descriptor the central gate derives for an absent key -- so the Create
  // button is offered there rather than the header losing its first button entirely.
  const skipperReportFeature = features[SKIPPER_REPORT_FEATURE] ?? { available: false, disabled: false, hidden: false }
  // The free-during-Beta flag rides the raw payload, not the decorated gate shape.
  const skipperReportBeta = true === getFeature(SKIPPER_REPORT_FEATURE)?.beta
  // A boolean, not the descriptor: the absent-key fallback above is a fresh object each render,
  // and an effect keyed on it would stop and restart the poll on every one.
  const isSkipperReportOn = isSkipperReportPurchased(skipperReportFeature)

  const controlsContainer = document.querySelector(`.${headerStyle['report-controls']}`)

  const [state, dispatch] = useReducer(reportsReducer, {})

  const currentModel = report
  const currentState = state[currentModel.id] || {}

  const isAdding = 'add' == currentState.process
  const isXpert = ['xpert', 'xpert_custom'].includes(currentModel.current.type)
  const isEmpty = null !== currentModel.current.schema && !currentModel.current.schema.length && !isXpert
  const isDisabled = 250 <= reports.length || disabled // true === state.disabled
  const isLocked = ['widget'].includes(currentModel.current.type) || isAdding || isSurveyGuest || isSurveyViewer
  const isBlankAllowed = !features.data_lab_vq.disabled && !isSurveyGuest && !isSurveyViewer
  // Mirror the backend 3-in-flight cap: every Skipper Report in draft/generating counts,
  // the auto-launched one included.
  const inflightSkipperCount = reports.filter(
    item => 'skipper' == item.current.type && ['draft', 'generating'].includes(item.current.status)
  ).length

  const dndDefaultItemsListRef = useRef(null)
  const dndAutomatedItemsListRef = useRef(null)
  const dndGuidedItemsListRef = useRef(null)
  const dndOtherItemsListRef = useRef(null)

  const dndReportTypeRef = useRef(null)
  const dndReportPositionRef = useRef(null)

  const dndUpdateTimerRef = useRef(null)

  const componentRef = useRef(null)

  const [root, setRoot] = useState(null)

  const setComponentRef = useCallback(node => {
    componentRef.current = node
    setRoot(node?.parentNode ?? null)
  }, [])

  const togglePlaceholder = (placeholderIndex, dndItemsListRef) => {
    const placeholder = dndItemsListRef.current.querySelector(`.${style.placeholder}`)
    if (null === placeholder) return

    if (
      0 == placeholderIndex &&
      1 == dndReportPositionRef.current &&
      dndItemsListRef.current.classList.contains('stats-ReportsPanel-listOther')
    ) {
      placeholder.style.display = 'none'
    } else placeholder.style.display = null === placeholderIndex ? 'none' : 'block'
  }

  const buildPlaceholder = dndItemsListRef => {
    const placeholderNode = document.createElement('div')
    placeholderNode.className = style.placeholder
    if (null !== dndReportTypeRef.current) {
      placeholderNode.classList.add(style[`placeholder-${dndReportTypeRef.current}`])
    }
    if (dndItemsListRef.current.classList.contains('stats-ReportsPanel-listOther')) {
      placeholderNode.style.display = 'none'
    }
    return placeholderNode
  }

  const onDNDUpdateItemPositions = useCallback(({ placeholderIndex, dndItemsListRef }) => {
    if (null === dndItemsListRef.current) return

    togglePlaceholder(placeholderIndex, dndItemsListRef)
    if (dndUpdateTimerRef.current) clearTimeout(dndUpdateTimerRef.current)
    dndUpdateTimerRef.current = setTimeout(() => {
      togglePlaceholder(placeholderIndex, dndItemsListRef)
    }, 10)
  }, [])

  const onDNDDropDefault = useCallback(
    dropData => {
      const { item } = dropData

      if (null !== item && null !== item.id) {
        const cloneModel = Object.assign({}, item)

        dropListItemDefault({
          url: PageGlobals.STAT_REPORT_URL,
          listModel: reports,
          model: cloneModel,
          onReportsChange,
          onReportsError
        })
      }
    },
    [reports, onReportsChange, onReportsError]
  )
  const onDNDDropAutomated = useCallback(
    dropData => {
      const { item } = dropData

      if (null !== item && null !== item.id) {
        const cloneModel = Object.assign({}, item)

        dropListItemAutomated({
          url: PageGlobals.STAT_REPORT_URL,
          listModel: reports,
          model: cloneModel,
          onReportsChange,
          onReportsError
        })
      }
    },
    [reports, onReportsChange, onReportsError]
  )
  const onDNDDropGuided = useCallback(
    dropData => {
      const { item, position } = dropData

      if (null !== item && null !== item.id && null !== position) {
        dropListItemGuided({
          url: PageGlobals.STAT_REPORT_URL,
          listIndex: position,
          listModel: reports,
          model: Object.assign({}, item),
          onReportsChange,
          onReportsError
        })
      }
    },
    [reports, onReportsChange, onReportsError]
  )
  const onDNDDropOther = useCallback(
    dropData => {
      const { item, position } = dropData

      if (null !== item && null !== item.id && null !== position) {
        const cloneModel = Object.assign({}, item)

        dropListItemOther({
          url: PageGlobals.STAT_REPORT_URL,
          listIndex: position,
          listModel: reports,
          model: cloneModel,
          onReportsChange,
          onReportsError
        })
      }
    },
    [reports, onReportsChange, onReportsError]
  )

  const dndDefaultItemsList = useMemo(
    () =>
      ADND.targets
        .itemsList(
          {
            acceptTypes: [
              `report-${PageGlobals.WINDOW_UID}`,
              `report-${PageGlobals.WINDOW_UID}-skipper`,
              `report-${PageGlobals.WINDOW_UID}-concept`,
              `report-${PageGlobals.WINDOW_UID}-skipper-guided`
            ]
          },
          { capture: false }
        )
        .buildPlaceholder(() => buildPlaceholder(dndDefaultItemsListRef))
        .onUpdateItemPositions(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndDefaultItemsListRef })
        )
        .onEnter(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndDefaultItemsListRef })
        )
        .onLeave(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndDefaultItemsListRef })
        )
        .onDrop(onDNDDropDefault),
    [onDNDUpdateItemPositions, onDNDDropDefault]
  )
  const dndAutomatedItemsList = useMemo(
    () =>
      ADND.targets
        .itemsList(
          {
            acceptTypes: [
              `report-${PageGlobals.WINDOW_UID}-skipper`,
              `report-${PageGlobals.WINDOW_UID}-skipper-hidden`,
              `report-${PageGlobals.WINDOW_UID}-concept`,
              `report-${PageGlobals.WINDOW_UID}-concept-hidden`
            ]
          },
          { capture: false }
        )
        .buildPlaceholder(() => buildPlaceholder(dndAutomatedItemsListRef))
        .onUpdateItemPositions(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndAutomatedItemsListRef })
        )
        .onEnter(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndAutomatedItemsListRef })
        )
        .onLeave(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndAutomatedItemsListRef })
        )
        .onDrop(onDNDDropAutomated),
    [onDNDUpdateItemPositions, onDNDDropAutomated]
  )
  const dndGuidedItemsList = useMemo(
    () =>
      ADND.targets
        .itemsList(
          {
            acceptTypes: [
              `report-${PageGlobals.WINDOW_UID}-skipper-guided`,
              `report-${PageGlobals.WINDOW_UID}-skipper-guided-hidden`
            ]
          },
          { capture: false }
        )
        .buildPlaceholder(() => buildPlaceholder(dndGuidedItemsListRef))
        .onUpdateItemPositions(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndGuidedItemsListRef })
        )
        .onEnter(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndGuidedItemsListRef })
        )
        .onLeave(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndGuidedItemsListRef })
        )
        .onDrop(onDNDDropGuided),
    [onDNDUpdateItemPositions, onDNDDropGuided]
  )
  const dndOtherItemsList = useMemo(
    () =>
      ADND.targets
        .itemsList(
          {
            acceptTypes: [`report-${PageGlobals.WINDOW_UID}`, `report-${PageGlobals.WINDOW_UID}-hidden`]
          },
          { capture: false }
        )
        .buildPlaceholder(() => buildPlaceholder(dndOtherItemsListRef))
        .onUpdateItemPositions(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndOtherItemsListRef })
        )
        .onEnter(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndOtherItemsListRef })
        )
        .onLeave(({ placeholderIndex }) =>
          onDNDUpdateItemPositions({ placeholderIndex, dndItemsListRef: dndOtherItemsListRef })
        )
        .onDrop(onDNDDropOther),
    [onDNDUpdateItemPositions, onDNDDropOther]
  )

  useEffect(() => {
    dndDefaultItemsList.connect(dndDefaultItemsListRef.current)

    return () => dndDefaultItemsList.disconnect()
  }, [dndDefaultItemsList])
  useEffect(() => {
    dndAutomatedItemsList.connect(dndAutomatedItemsListRef.current)

    return () => dndAutomatedItemsList.disconnect()
  }, [dndAutomatedItemsList])
  useEffect(() => {
    dndGuidedItemsList.connect(dndGuidedItemsListRef.current)

    return () => dndGuidedItemsList.disconnect()
  }, [dndGuidedItemsList])
  useEffect(() => {
    dndOtherItemsList.connect(dndOtherItemsListRef.current)

    return () => dndOtherItemsList.disconnect()
  }, [dndOtherItemsList])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (dndUpdateTimerRef.current) clearTimeout(dndUpdateTimerRef.current)
    }
  }, [])

  const renderItems = (items, d, dndItemsList) =>
    items.map((itemModel, itemIndex) => (
      <Report
        key={itemModel.id}
        model={itemModel}
        reportId={reportId}
        position={itemIndex + d}
        dndItemsList={dndItemsList}
        dndReportTypeRef={dndReportTypeRef}
        dndReportPositionRef={dndReportPositionRef}
        onReportIdChange={onReportIdChange}
      />
    ))

  const renderList = () => {
    // The slot takes the first non-virtual report wherever it sits, so the groups have to draw
    // from the list minus THAT report rather than minus index 0 -- otherwise a virtual report at
    // index 0 leaves the slot's pick still inside the groups, rendering it twice.
    const defaultItemsSeq = reports.filter(r => !VIRTUAL_REPORTS_IDS.includes(r.current.type)).slice(0, 1)
    const defaultItem = defaultItemsSeq[0]
    const groupedItems = reports.filter(r => r !== defaultItem)

    const skipperItemsSeq = groupedItems.filter(r => 'skipper' == r.current.type && !isSkipperGuidedReport(r.current))

    const conceptItemsSeq = groupedItems.filter(r => 'concept' == r.current.type)

    const virtualItemsSeq = groupedItems.filter(r => VIRTUAL_REPORTS_IDS.includes(r.current.type))

    const skipperGuidedItemsSeq = groupedItems.filter(
      r => 'skipper' == r.current.type && isSkipperGuidedReport(r.current)
    )

    const otherItemsSeq = groupedItems.filter(
      r => ![...STATIC_REPORTS_IDS, ...VIRTUAL_REPORTS_IDS].includes(r.current.type)
    )

    return (
      <>
        {0 < defaultItemsSeq.length ? (
          <div
            ref={dndDefaultItemsListRef}
            data-testid="stat-filters-default-report"
            className={classNames(style.list, style['list-default'])}
          >
            <div className={`${style.label} ${style['label-default']}`}>Default Report</div>
            {renderItems(defaultItemsSeq, 1, dndDefaultItemsList)}
          </div>
        ) : null}
        {0 < skipperItemsSeq.length || 0 < conceptItemsSeq.length || 0 < virtualItemsSeq.length ? (
          <div
            ref={dndAutomatedItemsListRef}
            data-testid="stat-filters-automated-reports"
            className={classNames(style.list, style['list-automated'])}
          >
            <div className={style.label}>Automated Reports</div>
            {0 < skipperItemsSeq.length
              ? renderItems(skipperItemsSeq, defaultItemsSeq.length + 1, dndAutomatedItemsList)
              : null}
            {0 < conceptItemsSeq.length
              ? renderItems(conceptItemsSeq, defaultItemsSeq.length + skipperItemsSeq.length + 1, dndAutomatedItemsList)
              : null}
            {0 < virtualItemsSeq.length
              ? renderItems(
                  virtualItemsSeq,
                  defaultItemsSeq.length + skipperItemsSeq.length + conceptItemsSeq.length + 1
                )
              : null}
          </div>
        ) : null}
        {0 < skipperGuidedItemsSeq.length ? (
          <div
            ref={dndGuidedItemsListRef}
            data-testid="stat-filters-guided-reports"
            className={classNames(style.list, style['list-guided'])}
          >
            <div className={style.label}>Guided Reports</div>
            {renderItems(
              skipperGuidedItemsSeq,
              defaultItemsSeq.length + skipperItemsSeq.length + conceptItemsSeq.length + virtualItemsSeq.length + 1,
              dndGuidedItemsList
            )}
          </div>
        ) : null}
        {0 < otherItemsSeq.length && !isSurveyGuest ? (
          <div
            ref={dndOtherItemsListRef}
            data-testid="stat-filters-other-reports"
            className={classNames(style.list, style['list-other'])}
          >
            <div className={style.label}>Other Reports</div>
            {renderItems(
              otherItemsSeq,
              defaultItemsSeq.length +
                skipperItemsSeq.length +
                conceptItemsSeq.length +
                virtualItemsSeq.length +
                skipperGuidedItemsSeq.length +
                1,
              dndOtherItemsList
            )}
          </div>
        ) : null}
      </>
    )
  }

  const onReportAdd = () => {
    addListItem(dispatch, {
      url: PageGlobals.STAT_REPORT_URL,
      listModel: reports,
      model: currentModel,
      onReportsChange,
      onReportsError
    })
  }

  const onReportBlankAdd = () => {
    const itemModel = { current: {} }
    itemModel.current['params'] = {}
    itemModel.current['config'] = {}
    itemModel.current['virtual'] = {
      vq1: {
        id: 'vq1',
        text: 'Virtual Question',
        type: 'dlv',
        variant: 'rb',
        answerVariants: [
          {
            text: '',
            pattern: '',
            position: 1,
            options: {
              uid: generateUID(),
              mode: 'basic'
            }
          },
          {
            text: '',
            pattern: '',
            position: 2,
            options: {
              uid: generateUID(),
              mode: 'basic'
            }
          }
        ],
        position: 0
      }
    }
    itemModel.current['schema'] = ['vq1']
    itemModel.current['storytellings'] = {}
    itemModel.current['storytellingNotes'] = {}
    itemModel.current['logicData'] = {}
    itemModel.current['scripts'] = null

    addListItem(dispatch, {
      url: PageGlobals.STAT_REPORT_URL,
      listModel: reports,
      model: itemModel,
      onReportsChange,
      onReportsError
    })
  }

  // The list the poll callbacks read: they fire long after the render that armed them, so they
  // must not close over a stale `reports`.
  const reportsRef = useRef(reports)
  reportsRef.current = reports

  // Watches the chat for the launch handle so a just-created draft can flip to "generating"
  // in-session.
  const generationPollRef = useRef(null)
  // Tracks the stat-server generation job (via the handle's polling_url) to its terminal state so
  // the draft can flip to "ready" / settle on failure.
  const jobPollRef = useRef(null)
  // Identity of the generation the job poll is tracking -- so the same handle arriving again does
  // not restart the poll; only a new generation does.
  const polledGenerationKeyRef = useRef(null)
  // The report this panel claimed for the job poll, released when the poll ends.
  const jobPollReportIdRef = useRef(null)

  // Drop the job poll and hand the report back, so the status poll may watch it again.
  const releaseGuidedReportJobPoll = useCallback(() => {
    jobPollRef.current?.stop()
    jobPollRef.current = null
    releaseGuidedReportTracking(jobPollReportIdRef.current)
    jobPollReportIdRef.current = null
  }, [])

  const holdsReport = useCallback(reportId => {
    if (!reportId) return false
    return 0 <= reportsRef.current.findIndex(item => reportId == item.id)
  }, [])

  // Consume the launch handle's polling_url to track the generation in-session. On completion flip
  // the draft to "ready" (the row offers a view affordance); on failure settle it so the user can
  // read the failure note. No handle URL means nothing to poll. Idempotent on the handle identity.
  const startGuidedReportJobPoll = useCallback(
    generation => {
      const key = generationPollKey(generation)
      if (!key || key === polledGenerationKeyRef.current) return
      if (jobPollRef.current) return // a generation is already being tracked

      const reportId = generationReportId(generation)
      // This poll outranks the status poll, so it displaces one already watching the report rather
      // than standing down: only this one carries the persisted report and writes a terminal state.
      const claimed = claimGuidedReportTracking(reportId, {
        rank: TRACKING_RANK.generation,
        stop: () => releaseGuidedReportJobPoll()
      })
      if (reportId && !claimed) return

      polledGenerationKeyRef.current = key
      jobPollReportIdRef.current = claimed ? reportId : null

      jobPollRef.current = createGuidedReportJobPoll({
        pollingUrl: generation.polling_url,
        jobId: generation.job_id,
        onReport: report => {
          // This reading is what the job poll was for -- it carries the report the stat server
          // persisted at launch. The claim goes back before the write below, because that write is
          // what arms the id-based status poll and a claim still held would refuse it.
          releaseGuidedReportJobPoll()

          adoptSkipperGuidedDraftReport({
            listModel: reportsRef.current,
            onReportsChange,
            reportId,
            report
          })
          // the reading swaps the placeholder skeleton for the server's body, and a user sitting
          // on that row is looking at an area drawn from the skeleton
          repaintReport?.(reportId)
        },
        onReady: report => {
          markSkipperGuidedDraftReady({
            listModel: reportsRef.current,
            onReportsChange,
            reportId,
            report
          })
          releaseGuidedReportJobPoll()
          // nothing else draws the body that just landed on a row the user is already reading
          repaintReport?.(reportId)
        },
        // A newer turn superseded this generation: the row stays 'generating' untouched and the
        // claim goes back, so the winning generation may be watched on it.
        onSuperseded: () => releaseGuidedReportJobPoll(),
        onError: () => {
          // A failure settles the row for viewing like a completion does, and for the same reason:
          // the report persisted at launch is what holds the generation's own failure note, and the
          // view affordance is the only thing that opens it.
          markSkipperGuidedDraftReady({ listModel: reportsRef.current, onReportsChange, reportId })
          releaseGuidedReportJobPoll()

          if ('function' == typeof window.notifyStickyShow) {
            window.notifyStickyShow(
              'guided-report-error',
              'Skipper Report',
              GUIDED_REPORT_ERROR_NOTICE,
              'ico/error.png'
            )
          }
        }
      })
      jobPollRef.current.start()
    },
    [onReportsChange, releaseGuidedReportJobPoll, repaintReport]
  )

  const startGuidedReportGenerationPoll = useCallback(() => {
    if (generationPollRef.current) return // already watching this survey's dispatch

    generationPollRef.current = createGuidedReportGenerationPoll({
      onGeneration: (generation, { recovered }) => {
        // Read before the flip below, which re-ids a waiting draft onto this very report.
        const known = holdsReport(generationReportId(generation))

        // Claimed before the flip, not after: the flip's list write reaches the host synchronously
        // and arms the id-based status poll, which reads the claim to decide whether to start. A
        // report the list already holds is left to that poll.
        if (!known) startGuidedReportJobPoll(generation)

        // Create-or-flip: flip a waiting draft, flip the report a regeneration targets, or
        // materialise a generating placeholder when the handle is recovered on mount with no draft.
        ensureSkipperGuidedGeneratingDraft({
          listModel: reportsRef.current,
          onReportsChange,
          generation,
          recovered
        })
      }
    })
    generationPollRef.current.start()
  }, [holdsReport, startGuidedReportJobPoll, onReportsChange])

  // Recover a generation dispatched before this mount (a reload mid-generation). The handle is only
  // acted on while fresh, and ensureSkipperGuidedGeneratingDraft ignores one whose report has
  // already landed, so a completed generation cannot resurrect a placeholder.
  useEffect(() => {
    if (isSkipperReportOn) startGuidedReportGenerationPoll()

    return () => {
      generationPollRef.current?.stop()
      generationPollRef.current = null
      releaseGuidedReportJobPoll()
    }
  }, [isSkipperReportOn, startGuidedReportGenerationPoll, releaseGuidedReportJobPoll])

  // Status pollers, one per guided report the backend currently marks 'generating'. The
  // readback answers lifecycle fields only -- it carries no report -- so a row that settles
  // this way is marked ready and offers the view affordance, which is what fetches the body.
  const statusPollersRef = useRef({})

  const settleGuidedReport = useCallback(
    reportId => markSkipperGuidedDraftReady({ listModel: reportsRef.current, onReportsChange, reportId }),
    [onReportsChange]
  )

  // Reconcile the running pollers with the set the list currently marks 'generating'.
  // Local drafts (negative id) never match.
  useEffect(() => {
    const generating = new Set(
      reports.filter(r => 'skipper' == r.current.type && 'generating' == r.current.status && 0 < r.id).map(r => r.id)
    )
    const pollers = statusPollersRef.current

    // A poller the generation job poll displaced is dormant for good, so it is dropped here as
    // well as one whose report has left 'generating' -- otherwise its entry blocks a fresh poller
    // and a report the job poll later hands back would go unwatched.
    for (const [id, poller] of Object.entries(pollers)) {
      if (generating.has(Number(id)) && !poller.isStopped()) continue
      poller.stop()
      delete pollers[id]
    }

    generating.forEach(id => {
      if (pollers[id]) return
      pollers[id] = new GuidedReportStatusPoller(id, {
        onReady: reportId => settleGuidedReport(reportId),
        onStop: (reportId, reason) => {
          if (['ready', 'untracked'].includes(reason)) return

          // A failed generation delivers no report and the row would spin until the next page
          // load. Settle it for viewing: opening it serves whatever the generation left behind,
          // its own failure note included.
          settleGuidedReport(reportId)

          if ('function' == typeof window.notifyStickyShow) {
            window.notifyStickyShow(
              'guided-report-error',
              'Skipper Report',
              GUIDED_REPORT_ERROR_NOTICE,
              'ico/error.png'
            )
          }
        }
      }).start()
    })
  }, [reports, settleGuidedReport])

  useEffect(() => {
    const pollers = statusPollersRef.current
    return () => {
      Object.values(pollers).forEach(poller => poller.stop())
      statusPollersRef.current = {}
    }
  }, [])

  const onSkipperGuidedReportAdd = () => {
    // Only one Skipper draft should exist at a time -- if one is already present, switch to it
    // instead of spawning a duplicate.
    const draft = reports.find(item => isSkipperGuidedDraftReport(item.current))

    if (draft) {
      onReportIdChange(draft.id)
    } else {
      addSkipperGuidedListItem({
        url: PageGlobals.STAT_REPORT_URL,
        listModel: reports,
        onReportsChange
      })
      startGuidedReportGenerationPoll()
    }

    openGuidedReportsChat({ entry: 'create' })
  }

  const onConfigClick = useCallback(() => {
    onPanelChange('reports')

    dispatch({
      type: 'UPDATE_REPORT_STATE',
      request: {
        id: currentModel.id,
        status: 'active'
      }
    })
  }, [currentModel.id, onPanelChange])

  const dataContextValue = useMemo(() => {
    return {
      root,
      state,
      mode,
      listModel: reports,
      features,
      isAdmin,
      isSurveyGuest,
      isSurveyViewer,
      isSurveyCompleted,
      disabled,
      dispatch,
      getReportFilters,
      onReportsChange,
      onReportsError
    }
  }, [
    root,
    state,
    mode,
    reports,
    features,
    isAdmin,
    isSurveyGuest,
    isSurveyViewer,
    isSurveyCompleted,
    disabled,
    dispatch,
    getReportFilters,
    onReportsChange,
    onReportsError
  ])

  return (
    <>
      {isHidden ? null : (
        <>
          <SidebarPopupWarning isQAMode={isQAMode} disabled={disabled} onModeChange={onModeChange} />

          <div ref={setComponentRef} className={style.component}>
            <div className={style['component-title']}>
              Reports
              {root && <SidebarPopup id="reports" root={root} help={REPORTS_HELP} />}
            </div>

            {0 < reports.length && (
              <>
                {isSurveyGuest || isSurveyViewer ? null : (
                  <div className={style.header}>
                    {skipperReportFeature?.hidden ? (
                      <div className="p-fluid p-col">
                        <Button
                          data-testid="stat-filters-save-report"
                          className="p-button-rounded p-button-blue"
                          label="Save as a new report"
                          icon={<i className="icon-aytmfd-report-v2" />}
                          disabled={isEmpty || isDisabled || isLocked}
                          onClick={isEmpty || isDisabled || isLocked ? null : onReportAdd}
                        />
                      </div>
                    ) : (
                      <div className="p-fluid p-col">
                        <SkipperGuidedReportButton
                          feature={skipperReportFeature}
                          betaFree={skipperReportBeta}
                          atInflightCap={inflightSkipperCount >= MAX_INFLIGHT_REPORTS}
                          atReportsCap={250 <= reports.length}
                          isSurveyCompleted={isSurveyCompleted}
                          onCreate={onSkipperGuidedReportAdd}
                        />
                      </div>
                    )}
                    {isBlankAllowed && (
                      <div className="p-fluid p-col">
                        <Button
                          data-testid="stat-filters-add-report"
                          className="p-button-rounded p-button-blue p-button-secondary"
                          label="Create a blank report"
                          icon={<i className="icon-aytmfd-report-v2" />}
                          disabled={isXpert || isDisabled || isLocked}
                          onClick={isXpert || isDisabled || isLocked ? null : onReportBlankAdd}
                        />
                      </div>
                    )}
                  </div>
                )}
                <DataContext.Provider value={dataContextValue}>{renderList()}</DataContext.Provider>
              </>
            )}
          </div>
        </>
      )}
      {null !== controlsContainer ? (
        <DataContext.Provider value={dataContextValue}>
          {createPortal(
            <>
              <ReportControls model={currentModel} isCompact />
              <span
                className={headerStyle['report-controls-config']}
                aria-label="Report settings"
                {...pressable(onConfigClick)}
              >
                <i className="icon-aytmfd-config" />
              </span>
            </>,
            controlsContainer
          )}
        </DataContext.Provider>
      ) : null}
    </>
  )
}
ReportsPanel.propTypes = {
  model: PropTypes.object.isRequired,
  report: PropTypes.object.isRequired,
  features: PropTypes.object.isRequired,
  isHidden: PropTypes.bool,
  getReportFilters: PropTypes.func,
  onModeChange: PropTypes.func,
  onPanelChange: PropTypes.func,
  onReportsChange: PropTypes.func,
  onReportsError: PropTypes.func,
  onReportIdChange: PropTypes.func,
  repaintReport: PropTypes.func
}

ReportsPanel.displayName = 'ReportsPanel'

export default ReportsPanel
